// Lightweight, provider-agnostic analytics layer.
//
// Backed by Firebase Analytics (GA4) using the Firebase app the rest of the
// site already initializes. It is intentionally defensive:
//
//  - If REACT_APP_FIREBASE_MEASUREMENT_ID is not set, everything no-ops, so this
//    is safe to ship before Google Analytics is switched on in Firebase.
//  - Analytics must never break the app: every call is wrapped so a failure in
//    tracking can't throw into product code.
//  - Events fired before init completes (isSupported is async) are queued and
//    flushed once analytics is ready.
//
// Call sites use `track(name, params)` and `identify(userId)` and never import
// Firebase directly, so the backend can be swapped (Segment, Sentry, etc.)
// without touching product code.

import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
} from "firebase/analytics";
import app from "../firebase/config";

const MEASUREMENT_ID = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;
const MAX_QUEUE = 50;

let analytics = null;
let ready = false;
let pendingUserId; // undefined = untouched; null = explicitly signed out
const queue = [];

/** True when a Measurement ID is configured (so a consent banner is warranted). */
export function isAnalyticsConfigured() {
  return !!MEASUREMENT_ID;
}

/**
 * Initialize analytics. Safe to call once on app startup. No-ops (silently)
 * when no measurement id is configured or the browser doesn't support it.
 */
export async function initAnalytics() {
  try {
    if (!MEASUREMENT_ID) return;
    if (!(await isSupported())) return;

    analytics = getAnalytics(app);
    ready = true;

    if (pendingUserId !== undefined) setUserId(analytics, pendingUserId);
    while (queue.length) {
      const { name, params } = queue.shift();
      logEvent(analytics, name, params);
    }
  } catch (err) {
    // Analytics must never break the app.
  }
}

/**
 * Record an event. Params should be low-cardinality and free of PII
 * (ids are fine, emails / card data are not).
 */
export function track(name, params = {}) {
  try {
    if (!MEASUREMENT_ID) return; // not configured — drop silently
    if (ready && analytics) {
      logEvent(analytics, name, params);
    } else if (queue.length < MAX_QUEUE) {
      // Buffer events fired before init/consent completes; flushed on init.
      // Capped so a session that never consents can't grow this unbounded.
      queue.push({ name, params });
    }
  } catch (err) {
    // swallow
  }
}

/** Associate subsequent events with a user id (pass null on sign-out). */
export function identify(userId) {
  try {
    if (!MEASUREMENT_ID) return;
    if (ready && analytics) {
      setUserId(analytics, userId || null);
    } else {
      pendingUserId = userId || null;
    }
  } catch (err) {
    // swallow
  }
}
